<template>
  <BaseDialog
    v-model="dialog"
    title="Compare Revisions"
    max-width="1600"
    content-class="pa-0 d-flex overflow-hidden"
  >
    <div class="dialog-container">
      <div class="d-flex flex-column">
        <v-list-item
          title="Asset Management Plan"
          :subtitle="master.name ?? ''"
        />
        <v-divider />
        <v-list v-model="tabKey">
          <v-list-item
            v-for="section in sections"
            :key="section.key"
            link
            :title="section.title"
            :active="section.key === tabKey"
            density="compact"
            @click="tabKey = section.key"
          />
        </v-list>
      </div>

      <v-divider vertical />

      <div class="compare-container">
        <div class="compare-header">
          <div class="select-revisions">
            <v-autocomplete
              class="px-2"
              style="width: 50%"
              :model-value="prevRevision"
              label="Previous Revision"
              :items="master.asset_management_plans"
              item-value="id"
              item-title="revision"
              return-object
              density="compact"
              hide-details
              :loading="loadingPrev"
              @update:model-value="getPrevRevision($event)"
            />
            <v-autocomplete
              class="px-2"
              style="width: 50%"
              :model-value="currRevision"
              label="Current Revision"
              :items="master.asset_management_plans"
              item-value="id"
              item-title="revision"
              return-object
              density="compact"
              hide-details
              :loading="loadingCurr"
              @update:model-value="getCurrRevision($event)"
            />
          </div>
          <v-divider />
        </div>
        <div class="d-flex">
          <template v-if="tabKey === 'general'">
            <DiffGeneral :plan="prevRevision" />
            <DiffGeneral :plan="currRevision" :diff-plan="prevRevision" diff />
          </template>
          <template v-else-if="tabKey === 'introduction'">
            <DiffIntroduction :plan="prevRevision" />
            <DiffIntroduction
              :plan="currRevision"
              :diff-plan="prevRevision"
              diff
            />
          </template>
          <template v-else-if="tabKey === 'raci'">
            <DiffRACI :plan="prevRevision" />
            <DiffRACI :plan="currRevision" :diff-plan="prevRevision" diff />
          </template>
          <template v-else-if="tabKey === 'scope-boundary-limits'">
            <DiffScopeBoundaryLimits :plan="prevRevision" />
            <DiffScopeBoundaryLimits
              :plan="currRevision"
              :diff-plan="prevRevision"
              diff
            />
          </template>
          <template v-else-if="tabKey === 'asset-information'">
            <DiffAssetInformation :plan="prevRevision" />
            <DiffAssetInformation
              :plan="currRevision"
              :diff-plan="prevRevision"
              diff
            />
          </template>
          <template v-else-if="tabKey === 'asset-management-plan'">
            <DiffAssetManagementPlan :plan="prevRevision" />
            <DiffAssetManagementPlan
              :plan="currRevision"
              :diff-plan="prevRevision"
              diff
            />
          </template>
          <template v-else-if="tabKey === 'service-levels-kpis'">
            <DiffServiceLevelKPIs :plan="prevRevision" />
            <DiffServiceLevelKPIs
              :plan="currRevision"
              :diff-plan="prevRevision"
              diff
            />
          </template>
          <template v-else-if="tabKey === 'life-cycle-cost-modelling'">
            <DiffLifeCycleCostModelling :plan="prevRevision" />
            <DiffLifeCycleCostModelling
              :plan="currRevision"
              :diff-plan="prevRevision"
              diff
            />
          </template>
          <template v-else-if="tabKey === 'improvement-actions'">
            <DiffImprovementActions :plan="prevRevision" />
            <DiffImprovementActions
              :plan="currRevision"
              :diff-plan="prevRevision"
              diff
            />
          </template>
        </div>
      </div>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import BaseDialog from "@/components/base/dialogs/BaseDialog.vue";
import { AssetManagementPlan, AssetManagementPlanMaster } from "@/classes";
import { sections } from "../sections";
import DiffGeneral from "./DiffGeneral.vue";
import DiffIntroduction from "./DiffIntroduction.vue";
import DiffRACI from "./DiffRACI.vue";
import DiffScopeBoundaryLimits from "./DiffScopeBoundaryLimits.vue";
import DiffAssetInformation from "./DiffAssetInformation.vue";
import DiffAssetManagementPlan from "./DiffAssetManagementPlan.vue";
import DiffServiceLevelKPIs from "./DiffServiceLevelKPIs.vue";
import DiffLifeCycleCostModelling from "./DiffLifeCycleCostModelling.vue";
import DiffImprovementActions from "./DiffImprovementActions.vue";

const props = defineProps<{
  master: AssetManagementPlanMaster;
}>();

const dialog = ref(false);
const currRevision = ref(new AssetManagementPlan());
const prevRevision = ref<AssetManagementPlan | null>(null);

const tabKey = ref<string>("general");

const open = async (revision: AssetManagementPlan) => {
  dialog.value = true;
  tabKey.value = "general";
  await getCurrRevision(revision);
  const index = props.master.asset_management_plans.findIndex(
    (r) => r.id === revision.id,
  );
  if (index >= 0 && index < props.master.asset_management_plans.length - 1) {
    await getPrevRevision(props.master.asset_management_plans[index + 1]);
  } else {
    prevRevision.value = null;
  }
};

const loadingPrev = ref(false);
const loadingCurr = ref(false);

const getPrevRevision = async (revision: AssetManagementPlan | null) => {
  loadingPrev.value = true;
  if (revision) {
    prevRevision.value = await AssetManagementPlan.get(revision.id);
    prevRevision.value.fixRelationships();
  } else {
    prevRevision.value = null;
  }
  loadingPrev.value = false;
};

const getCurrRevision = async (revision: AssetManagementPlan | null) => {
  if (!revision) return;
  loadingCurr.value = true;
  currRevision.value = await AssetManagementPlan.get(revision.id);
  currRevision.value.fixRelationships();
  loadingCurr.value = false;
};

defineExpose({ open });
</script>

<style scoped>
.dialog-container {
  display: flex;
  height: calc(80vh);
  max-height: 900px;
  width: 100%;
}

.compare-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.compare-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  display: flex;
  flex-direction: column;
}

.select-revisions {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  height: 48px;
}
</style>
